import type { EmbedPolicy } from "./launchClient";

export const SESSION_SURFACE_CONTRACT_VERSION = "1" as const;
export const WEB_SURFACE_PROFILE = "ato.web-surface.v1" as const;
export const PIXEL_STREAM_PROFILE = "ato.pixel-stream.v1" as const;
export const TERMINAL_SURFACE_PROFILE = "ato.terminal-surface.v1" as const;
// Bounds of the common PTY control protocol, shared by descriptor and transport.
export const MIN_TERMINAL_COLS = 2;
export const MAX_TERMINAL_COLS = 500;
export const MIN_TERMINAL_ROWS = 2;
export const MAX_TERMINAL_ROWS = 200;
export const BROWSER_SURFACE_PROFILE = "ato.browser-surface.v1" as const;
/** A Computation that is running and presents nothing to look at — a
 *  background process, a batch job. Distinct from "a surface has not arrived
 *  yet": this says one is never coming, so a client can stop waiting. It is a
 *  property of the workload, NOT of where it runs; a hosted headless process
 *  and a local one describe themselves the same way. */
export const HEADLESS_SURFACE_PROFILE = "ato.headless-surface.v1" as const;

export type AcceptedSessionSurface = {
  kind: "web" | "pixel_stream" | "terminal";
  profiles: readonly string[];
};

/**
 * Capabilities advertised by this PWA at launch-selection time. Keep this
 * list truthful and versioned: a runner is allocated only after the capsule,
 * runner, and client capability sets intersect.
 */
export function acceptedSessionSurfaces(options: { terminalEnabled: boolean }) {
  const surfaces: AcceptedSessionSurface[] = [
    { kind: "web", profiles: [WEB_SURFACE_PROFILE] },
    { kind: "pixel_stream", profiles: [PIXEL_STREAM_PROFILE] },
  ];
  if (options.terminalEnabled) {
    surfaces.push({ kind: "terminal", profiles: [TERMINAL_SURFACE_PROFILE] });
  }
  return surfaces;
}

export const PWA_ACCEPTED_SESSION_SURFACES = acceptedSessionSurfaces({
  terminalEnabled: import.meta.env.VITE_TERMINAL_SURFACE_ENABLED === "1",
});

export interface SessionSurfaceAccess {
  /** Public, token-free endpoint. Secrets are never represented by this type. */
  connectUrl: string;
  authExchangeUrl?: string;
  expiresAt?: string;
  generation: number;
  /** Physical availability for this live execution; never descriptor identity. */
  surfaceControls?: SurfaceOperationalControls;
}

export interface SurfaceOperationalControls {
  clipboard_paste: boolean;
  framebuffer_resize: boolean;
}

export interface PixelStreamSessionAccess extends SessionSurfaceAccess {
  authExchangeUrl: string;
  expiresAt: string;
}

export interface TerminalSessionAccess extends SessionSurfaceAccess {
  authExchangeUrl: string;
  expiresAt: string;
}

export interface WebSurfaceDescriptor {
  kind: "web";
  profile: typeof WEB_SURFACE_PROFILE;
  surfaceId: string | null;
  embedPolicy: EmbedPolicy;
}

export interface PixelStreamViewport {
  width: number;
  height: number;
}

export interface PixelStreamSurfaceDescriptor {
  kind: "pixel_stream";
  profile: typeof PIXEL_STREAM_PROFILE;
  surfaceId: string;
  transport: "rfb_websocket";
  viewport: PixelStreamViewport;
  capabilities: Readonly<Record<string, boolean | number | string>>;
}

export interface TerminalSurfaceDescriptor {
  kind: "terminal";
  profile: typeof TERMINAL_SURFACE_PROFILE;
  surfaceId: string;
  transport: "terminal_websocket";
  capabilities: {
    input: true;
    resize: true;
    clipboard: false;
    fileTransfer: false;
    recording: false;
    encoding: "utf-8";
    cols?: number;
    rows?: number;
  };
}

export interface BrowserSurfaceDescriptor {
  kind: "browser";
  profile: typeof BROWSER_SURFACE_PROFILE;
  surfaceId: string;
  transport: "jpeg_control_v1" | "activity_room_webrtc";
}

export interface BrowserSessionAccess {
  connectUrl?: string;
  activityId?: string;
  credential?: string;
  expiresAt: string;
  generation: number;
}

export interface BrowserSessionSurface {
  kind: "browser";
  contractVersion: typeof SESSION_SURFACE_CONTRACT_VERSION;
  source: "contract";
  descriptor: BrowserSurfaceDescriptor;
  access: BrowserSessionAccess;
}

export interface WebSessionSurface {
  kind: "web";
  contractVersion: typeof SESSION_SURFACE_CONTRACT_VERSION;
  source: "contract" | "legacy_app_url";
  descriptor: WebSurfaceDescriptor;
  access: SessionSurfaceAccess;
}

export interface PixelStreamSessionSurface {
  kind: "pixel_stream";
  contractVersion: typeof SESSION_SURFACE_CONTRACT_VERSION;
  source: "contract";
  descriptor: PixelStreamSurfaceDescriptor;
  access: PixelStreamSessionAccess;
}

export interface TerminalSessionSurface {
  kind: "terminal";
  contractVersion: typeof SESSION_SURFACE_CONTRACT_VERSION;
  source: "contract";
  descriptor: TerminalSurfaceDescriptor;
  access: TerminalSessionAccess;
}

export type UnsupportedSurfaceCode =
  | "invalid_session_surface"
  | "unsupported_session_surface"
  | "unsupported_surface_contract_version";

/** A ready response that the PWA cannot safely interpret or render. */
export interface UnsupportedSessionSurface {
  kind: "unsupported";
  code: UnsupportedSurfaceCode;
  message: string;
  contractVersion?: string;
  rawKind?: string;
}

/**
 * A running Computation with no surface, by design.
 *
 * The absence being described here is deliberate and final. Treating it as
 * "still starting" is what left a running local process showing "Starting
 * app…" forever, with no Stop control, because the client kept polling for
 * something that was never going to arrive.
 */
export interface HeadlessSessionSurface {
  kind: "none";
  contractVersion: typeof SESSION_SURFACE_CONTRACT_VERSION;
  source: "contract";
  descriptor: { kind: "none"; profile: typeof HEADLESS_SURFACE_PROFILE };
}

export type SessionSurface =
  | HeadlessSessionSurface
  | WebSessionSurface
  | PixelStreamSessionSurface
  | TerminalSessionSurface
  | BrowserSessionSurface
  | UnsupportedSessionSurface;

export interface SessionSurfaceEnvelope {
  surface_contract_version?: unknown;
  surface?: unknown;
  app_url?: unknown;
  embed_policy?: unknown;
  app_expires_at?: unknown;
}

/**
 * The single dual-read boundary for launch responses.
 *
 * - A present `surface` is authoritative, including malformed values.
 * - Only an absent `surface` may synthesize a Web surface from `app_url`.
 * - Unknown kinds/profiles are typed unsupported results, never Web fallbacks.
 */
export function sessionSurfaceFromEnvelope(
  envelope: SessionSurfaceEnvelope,
): SessionSurface | null {
  if (!Object.prototype.hasOwnProperty.call(envelope, "surface")) {
    return legacyWebSurface(envelope);
  }

  const contractVersion = envelope.surface_contract_version;
  if (contractVersion !== SESSION_SURFACE_CONTRACT_VERSION) {
    if (typeof contractVersion === "string" && contractVersion.length > 0) {
      return unsupported(
        "unsupported_surface_contract_version",
        `Session surface contract ${contractVersion} is not supported by this client.`,
        { contractVersion },
      );
    }
    return unsupported(
      "invalid_session_surface",
      "The session surface contract version is missing or malformed.",
    );
  }

  const surface = recordOrNull(envelope.surface);
  const descriptor = recordOrNull(surface?.descriptor);
  const access = recordOrNull(surface?.access);

  // Handled before the access requirement below: a headless surface has
  // nothing to connect to, so demanding access information would reject the
  // one shape that is defined by not having any.
  if (surface && descriptor && stringOrNull(descriptor.kind) === "none") {
    if (stringOrNull(descriptor.profile) !== HEADLESS_SURFACE_PROFILE) {
      return unsupported(
        "unsupported_session_surface",
        "The headless surface profile is not supported by this client.",
        { contractVersion, rawKind: "none" },
      );
    }
    return {
      kind: "none",
      contractVersion,
      source: "contract",
      descriptor: { kind: "none", profile: HEADLESS_SURFACE_PROFILE },
    };
  }

  if (!surface || !descriptor || !access) {
    return unsupported(
      "invalid_session_surface",
      "The session surface descriptor or access information is malformed.",
      { contractVersion },
    );
  }

  const rawKind = stringOrNull(descriptor.kind);
  const rawProfile = stringOrNull(descriptor.profile);
  if (!rawKind || !rawProfile) {
    return unsupported(
      "invalid_session_surface",
      "The session surface kind or profile is missing or malformed.",
      { contractVersion },
    );
  }
  if (rawKind === "web" && rawProfile !== WEB_SURFACE_PROFILE) {
    return unsupported(
      "unsupported_session_surface",
      `Web surface profile ${rawProfile} is not supported by this client.`,
      { contractVersion, rawKind },
    );
  }
  if (rawKind === "pixel_stream" && rawProfile !== PIXEL_STREAM_PROFILE) {
    return unsupported(
      "unsupported_session_surface",
      `Pixel stream profile ${rawProfile} is not supported by this client.`,
      { contractVersion, rawKind },
    );
  }
  if (rawKind === "terminal" && rawProfile !== TERMINAL_SURFACE_PROFILE) {
    return unsupported(
      "unsupported_session_surface",
      `Terminal surface profile ${rawProfile} is not supported by this client.`,
      { contractVersion, rawKind },
    );
  }
  if (rawKind === "browser" && rawProfile !== BROWSER_SURFACE_PROFILE) {
    return unsupported(
      "unsupported_session_surface",
      `Browser surface profile ${rawProfile} is not supported by this client.`,
      { contractVersion, rawKind },
    );
  }
  if (
    rawKind !== "web" &&
    rawKind !== "pixel_stream" &&
    rawKind !== "terminal" &&
    rawKind !== "browser"
  ) {
    return unsupported(
      "unsupported_session_surface",
      `Session surface ${rawKind} is not supported by this client.`,
      { contractVersion, rawKind },
    );
  }

  if (rawKind === "browser") {
    const surfaceId = stringOrNull(descriptor.surface_id);
    const transport = descriptor.transport;
    const expiresAt = stringOrNull(access.expires_at);
    const generation = nonNegativeIntegerOrNull(access.generation);
    const connectUrl = stringOrNull(access.connect_url) ?? undefined;
    const activityId = stringOrNull(access.activity_id) ?? undefined;
    const credential = stringOrNull(access.credential) ?? undefined;
    const validJpeg =
      transport === "jpeg_control_v1" && validHttpsUrl(connectUrl);
    const validRoom =
      transport === "activity_room_webrtc" && Boolean(activityId && credential);
    if (
      !surfaceId ||
      !expiresAt ||
      generation === null ||
      (!validJpeg && !validRoom)
    ) {
      return unsupported(
        "invalid_session_surface",
        "The browser surface descriptor or access information is malformed.",
        { contractVersion, rawKind },
      );
    }
    return {
      kind: "browser",
      contractVersion,
      source: "contract",
      descriptor: {
        kind: "browser",
        profile: BROWSER_SURFACE_PROFILE,
        surfaceId,
        transport,
      },
      access: {
        ...(connectUrl ? { connectUrl } : {}),
        ...(activityId ? { activityId } : {}),
        ...(credential ? { credential } : {}),
        expiresAt,
        generation,
      },
    };
  }

  const parsedAccess = parseAccess(access);
  if (!parsedAccess || !accessMatchesKind(rawKind, parsedAccess)) {
    return unsupported(
      "invalid_session_surface",
      "The session surface access information is malformed.",
      { contractVersion, rawKind },
    );
  }

  if (rawKind === "web") {
    const surfaceId = stringOrNull(descriptor.surface_id);
    const embedPolicy = embedPolicyOrNull(descriptor.embed_policy);
    if (!surfaceId || !embedPolicy) {
      return unsupported(
        "invalid_session_surface",
        "The Web surface descriptor is malformed.",
        { contractVersion, rawKind },
      );
    }
    return {
      kind: "web",
      contractVersion,
      source: "contract",
      descriptor: {
        kind: "web",
        profile: WEB_SURFACE_PROFILE,
        surfaceId,
        embedPolicy,
      },
      access: parsedAccess,
    };
  }

  if (rawKind === "terminal") {
    const surfaceId = stringOrNull(descriptor.surface_id);
    const capabilities = recordOrNull(descriptor.capabilities);
    const authExchangeUrl = parsedAccess.authExchangeUrl;
    const expiresAt = parsedAccess.expiresAt;
    if (
      !surfaceId ||
      descriptor.transport !== "terminal_websocket" ||
      !authExchangeUrl ||
      !expiresAt ||
      !capabilities ||
      capabilities.input !== true ||
      capabilities.resize !== true ||
      capabilities.clipboard !== false ||
      capabilities.file_transfer !== false ||
      capabilities.recording !== false ||
      capabilities.encoding !== "utf-8" ||
      ((capabilities.cols !== undefined || capabilities.rows !== undefined) &&
        (!boundedTerminalSize(
          capabilities.cols,
          MIN_TERMINAL_COLS,
          MAX_TERMINAL_COLS,
        ) ||
          !boundedTerminalSize(
            capabilities.rows,
            MIN_TERMINAL_ROWS,
            MAX_TERMINAL_ROWS,
          )))
    ) {
      return unsupported(
        "invalid_session_surface",
        "The terminal surface descriptor or access information is malformed.",
        { contractVersion, rawKind },
      );
    }
    return {
      kind: "terminal",
      contractVersion,
      source: "contract",
      descriptor: {
        kind: "terminal",
        profile: TERMINAL_SURFACE_PROFILE,
        surfaceId,
        transport: "terminal_websocket",
        capabilities: {
          input: true,
          resize: true,
          clipboard: false,
          fileTransfer: false,
          recording: false,
          encoding: "utf-8",
          ...(capabilities.cols === undefined
            ? {}
            : {
                cols: capabilities.cols as number,
                rows: capabilities.rows as number,
              }),
        },
      },
      access: {
        ...parsedAccess,
        authExchangeUrl,
        expiresAt,
      },
    };
  }

  const surfaceId = stringOrNull(descriptor.surface_id);
  const viewport = recordOrNull(descriptor.viewport);
  const capabilities = scalarRecordOrNull(descriptor.capabilities);
  const width = positiveIntegerOrNull(viewport?.width);
  const height = positiveIntegerOrNull(viewport?.height);
  if (
    !surfaceId ||
    descriptor.transport !== "rfb_websocket" ||
    !viewport ||
    width === null ||
    height === null ||
    !capabilities ||
    capabilities.resize === true
  ) {
    return unsupported(
      "invalid_session_surface",
      "The pixel stream surface descriptor is malformed.",
      { contractVersion, rawKind },
    );
  }

  const authExchangeUrl = parsedAccess.authExchangeUrl;
  const expiresAt = parsedAccess.expiresAt;
  if (!authExchangeUrl || !expiresAt) {
    return unsupported(
      "invalid_session_surface",
      "The pixel stream surface access information is malformed.",
      { contractVersion, rawKind },
    );
  }

  return {
    kind: "pixel_stream",
    contractVersion,
    source: "contract",
    descriptor: {
      kind: "pixel_stream",
      profile: PIXEL_STREAM_PROFILE,
      surfaceId,
      transport: "rfb_websocket",
      viewport: { width, height },
      capabilities,
    },
    access: {
      ...parsedAccess,
      authExchangeUrl,
      expiresAt,
    },
  };
}

function legacyWebSurface(
  envelope: SessionSurfaceEnvelope,
): WebSessionSurface | null {
  const appUrl = stringOrNull(envelope.app_url);
  if (!appUrl) return null;
  const expiresAt = stringOrNull(envelope.app_expires_at) ?? undefined;
  return {
    kind: "web",
    contractVersion: SESSION_SURFACE_CONTRACT_VERSION,
    source: "legacy_app_url",
    descriptor: {
      kind: "web",
      profile: WEB_SURFACE_PROFILE,
      surfaceId: null,
      embedPolicy: embedPolicyOrNull(envelope.embed_policy) ?? "sandboxed",
    },
    access: {
      connectUrl: appUrl,
      ...(expiresAt ? { expiresAt } : {}),
      generation: 0,
    },
  };
}

function parseAccess(
  access: Record<string, unknown>,
): SessionSurfaceAccess | null {
  const connectUrl = stringOrNull(access.connect_url);
  const expiresAt = stringOrNull(access.expires_at);
  const generation = nonNegativeIntegerOrNull(access.generation);
  const authExchangeUrl =
    access.auth_exchange_url === undefined
      ? undefined
      : (stringOrNull(access.auth_exchange_url) ?? null);
  const controls = access.surface_controls === undefined
    ? undefined : parseSurfaceControls(access.surface_controls);
  if (
    !connectUrl ||
    !expiresAt ||
    generation === null ||
    authExchangeUrl === null || controls === null
  ) {
    return null;
  }
  return {
    connectUrl,
    ...(authExchangeUrl ? { authExchangeUrl } : {}),
    expiresAt,
    generation,
    ...(controls ? { surfaceControls: controls } : {}),
  };
}

function parseSurfaceControls(value: unknown): SurfaceOperationalControls | null {
  const record = recordOrNull(value);
  if (!record || Object.keys(record).length !== 2 ||
    typeof record.clipboard_paste !== "boolean" ||
    typeof record.framebuffer_resize !== "boolean") return null;
  return { clipboard_paste: record.clipboard_paste, framebuffer_resize: record.framebuffer_resize };
}

function unsupported(
  code: UnsupportedSurfaceCode,
  message: string,
  detail: { contractVersion?: string; rawKind?: string } = {},
): UnsupportedSessionSurface {
  return { kind: "unsupported", code, message, ...detail };
}

function recordOrNull(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function scalarRecordOrNull(
  value: unknown,
): Readonly<Record<string, boolean | number | string>> | null {
  const record = recordOrNull(value);
  if (!record) return null;
  for (const entry of Object.values(record)) {
    if (
      typeof entry !== "boolean" &&
      typeof entry !== "string" &&
      !(typeof entry === "number" && Number.isSafeInteger(entry) && entry >= 0)
    ) {
      return null;
    }
  }
  return record as Readonly<Record<string, boolean | number | string>>;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

/** The three-value `EmbedPolicy` validation, shared with `submissionClient`'s
 *  wizard-preview projection so there is one list of legal policies. Callers
 *  decide what `null` means; every caller so far falls back to `sandboxed`. */
export function embedPolicyOrNull(value: unknown): EmbedPolicy | null {
  return value === "sandboxed" ||
    value === "trusted_unsandboxed" ||
    value === "external_only"
    ? value
    : null;
}

function positiveIntegerOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : null;
}

function nonNegativeIntegerOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? value
    : null;
}

function accessMatchesKind(
  kind: "web" | "pixel_stream" | "terminal",
  access: SessionSurfaceAccess,
): boolean {
  let connectUrl: URL;
  try {
    connectUrl = new URL(access.connectUrl);
  } catch {
    return false;
  }
  if (connectUrl.username || connectUrl.password || connectUrl.hash)
    return false;
  if (kind === "web" && connectUrl.protocol !== "https:") return false;
  if (
    (kind === "pixel_stream" || kind === "terminal") &&
    connectUrl.protocol !== "wss:"
  ) {
    return false;
  }

  // Authenticated Pixel Stream v1 never exposes a directly reachable RFB
  // socket. The same-host exchange must mint the host-only session cookie
  // before noVNC opens the WebSocket.
  if (
    (kind === "pixel_stream" || kind === "terminal") &&
    !access.authExchangeUrl
  )
    return false;
  if (!access.authExchangeUrl) return true;
  try {
    const exchangeUrl = new URL(access.authExchangeUrl);
    return (
      exchangeUrl.protocol === "https:" &&
      exchangeUrl.host === connectUrl.host &&
      !exchangeUrl.username &&
      !exchangeUrl.password &&
      !exchangeUrl.hash
    );
  } catch {
    return false;
  }
}

function validHttpsUrl(value: string | undefined): boolean {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === "https:" &&
      !parsed.username &&
      !parsed.password &&
      !parsed.hash
    );
  } catch {
    return false;
  }
}

function boundedTerminalSize(
  value: unknown,
  min: number,
  max: number,
): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= min &&
    value <= max
  );
}
